import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  BookOpen, ShareNetwork, Users, CurrencyDollar, Clock, Robot, TrendUp,
  Plus, Microphone, ArrowRight, Play
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const StatCard = ({ icon: Icon, label, value, trend, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="stat-card p-6 card-hover"
  >
    <div className="flex items-start justify-between mb-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={24} weight="duotone" />
      </div>
      {trend && (
        <span className={`text-xs font-medium ${trend > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</p>
    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
  </motion.div>
);

const QuickAction = ({ icon: Icon, label, description, onClick, testId }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    data-testid={testId}
    className="w-full p-6 bg-zinc-900/50 border border-white/5 rounded-2xl text-left hover:border-white/10 transition-colors duration-200"
  >
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center">
        <Icon size={28} className="text-amber-500" weight="duotone" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{label}</h3>
        <p className="text-sm text-zinc-400">{description}</p>
      </div>
      <ArrowRight size={20} className="text-zinc-600" />
    </div>
  </motion.button>
);

const RecentActivity = ({ items }) => (
  <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
    <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
      Recent Activity
    </h3>
    <div className="space-y-3">
      {items.map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.1 }}
          className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors"
        >
          <div className={`w-2 h-2 rounded-full ${item.status === 'published' ? 'bg-emerald-400' : item.status === 'scheduled' ? 'bg-blue-400' : 'bg-zinc-500'}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{item.title}</p>
            <p className="text-xs text-zinc-500">{item.time}</p>
          </div>
          <span className="text-xs text-zinc-500 uppercase">{item.type}</span>
        </motion.div>
      ))}
      {items.length === 0 && (
        <p className="text-sm text-zinc-500 text-center py-4">No recent activity</p>
      )}
    </div>
  </div>
);

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recentItems, setRecentItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, lessonsRes, postsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/lessons`),
        axios.get(`${API}/social-posts`)
      ]);
      
      setAnalytics(analyticsRes.data);
      
      // Combine and sort recent items
      const lessons = lessonsRes.data.slice(0, 3).map(l => ({
        title: l.title,
        type: 'lesson',
        status: l.status,
        time: new Date(l.created_at).toLocaleDateString()
      }));
      const posts = postsRes.data.slice(0, 3).map(p => ({
        title: p.caption?.substring(0, 50) || 'Untitled post',
        type: 'post',
        status: p.status,
        time: new Date(p.created_at).toLocaleDateString()
      }));
      
      setRecentItems([...lessons, ...posts].slice(0, 5));
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { icon: BookOpen, label: "Lessons Created", value: analytics?.total_lessons || 0, trend: 12, color: "bg-amber-500/20 text-amber-500" },
    { icon: ShareNetwork, label: "Scheduled Posts", value: analytics?.scheduled_posts || 0, trend: 8, color: "bg-cyan-500/20 text-cyan-500" },
    { icon: Users, label: "New Members", value: analytics?.new_members || 0, trend: 23, color: "bg-emerald-500/20 text-emerald-500" },
    { icon: CurrencyDollar, label: "Monthly Revenue", value: `$${analytics?.monthly_revenue?.toLocaleString() || 0}`, trend: 15, color: "bg-violet-500/20 text-violet-500" },
    { icon: Clock, label: "Pending Approvals", value: analytics?.total_lessons - analytics?.published_lessons || 0, color: "bg-rose-500/20 text-rose-500" },
    { icon: Robot, label: "Active Agents", value: analytics?.active_agents || 0, color: "bg-blue-500/20 text-blue-500" },
    { icon: TrendUp, label: "Conversions", value: analytics?.total_conversions || 0, trend: 18, color: "bg-pink-500/20 text-pink-500" },
    { icon: Play, label: "Published Lessons", value: analytics?.published_lessons || 0, color: "bg-teal-500/20 text-teal-500" },
  ];

  return (
    <div data-testid="home-dashboard">
      {/* Header */}
      <div className="mb-8">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-white mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Welcome back, {user?.name?.split(' ')[0] || 'Creator'}
        </motion.h1>
        <p className="text-zinc-400">Here's what's happening with your academy today.</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <QuickAction
          icon={Plus}
          label="Create New Content"
          description="Start a new lesson, post, or funnel"
          onClick={() => navigate('/lessons')}
          testId="create-content-btn"
        />
        <QuickAction
          icon={Microphone}
          label="Speak a Command"
          description="Use voice to control your studio"
          onClick={() => document.querySelector('[data-testid="voice-command-btn"]')?.click()}
          testId="speak-command-btn"
        />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, i) => (
          <StatCard key={i} {...stat} />
        ))}
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <RecentActivity items={recentItems} />
        </div>

        {/* Quick Stats Summary */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Quick Summary
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total Funnels</span>
              <span className="text-white font-semibold">{analytics?.total_funnels || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Active Funnels</span>
              <span className="text-white font-semibold">{analytics?.active_funnels || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Total Posts</span>
              <span className="text-white font-semibold">{analytics?.total_posts || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Funnel Visitors</span>
              <span className="text-white font-semibold">{analytics?.total_visitors || 0}</span>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-white/5">
            <Button
              onClick={() => navigate('/analytics')}
              data-testid="view-analytics-btn"
              className="w-full h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white transition-colors duration-200"
            >
              View Full Analytics
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
